<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-12">
        <div class="top-title-wrap">
          <div class="main-title">
            <span>{{ $t('Ad Metrics Calculator') }}</span>
          </div>
        </div>
        <div class="sub-title">Select a metric to calculate. We’ll guide you with the required inputs.</div>
      </div>
      <div class="col-md-5">
        <box class="profile-box">
          <div class="box-title">What do you want to calculate?</div>
          <div class="btn-metrics-wrap">
            <dsp-button :class="{active: calcName==='impCalc'}" @click="changeCalc('impCalc')">Impressions</dsp-button>
            <dsp-button :class="{active: calcName==='clickCalc'}" @click="changeCalc('clickCalc')">Clicks</dsp-button>
            <dsp-button :class="{active: calcName==='ctrCalc'}" @click="changeCalc('ctrCalc')">CTR</dsp-button>
            <dsp-button :class="{active: calcName==='cpmCalc'}" @click="changeCalc('cpmCalc')">CPM</dsp-button>
            <dsp-button :class="{active: calcName==='cpcCalc'}" @click="changeCalc('cpcCalc')">CPC</dsp-button>
            <dsp-button :class="{active: calcName==='cpaCalc'}" @click="changeCalc('cpaCalc')">CPA</dsp-button>
          </div>
          <div class="mt-2">
            <dsp-button class="w-100"
                        :class="{active: calcName==='costCalc'}"
                        @click="changeCalc('costCalc')">Total Cost
            </dsp-button>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'impCalc'">
          <div class="box-title">Impression Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Clicks</div>
                <dspInput v-model.number="clicks"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="clicks"
                          :data-vv-as="$t('Clicks')"
                          placeholder="Enter total clicks"
                          @keyup="calculateImpressions($event,'clicks')"
                          @input="onPositiveInput('clicks', $event)"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CTR</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="%">
                  <dspInput v-model.number="ctr"
                            v-validate="'max_value:100|min_value:0'"
                            :errorsBag="errors"
                            name="ctr"
                            :data-vv-as="$t('CTR')"
                            placeholder="Enter total CTR"
                            @keyup="calculateImpressions($event,'ctr')"
                            @input="onPositiveInput('ctr', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      IMP = {{ impressions ? impressions : 0 }}
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(impressions))" class="w-100">Copy Result
                    </dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetImp()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'clickCalc'">
          <div class="box-title">Clicks Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Impressions</div>
                <dspInput v-model.number="impressions1"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="impressions1"
                          :data-vv-as="$t('Impressions')"
                          placeholder="Enter total impressions"
                          @keyup="calculateClicks($event,'impressions1')"
                          @input="onPositiveInput('impressions1', $event)"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CTR</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="%">
                  <dspInput v-model.number="ctr1"
                            v-validate="'max_value:100|min_value:0'"
                            :errorsBag="errors"
                            name="ctr1"
                            :data-vv-as="$t('Ctr')"
                            placeholder="Enter total CTR"
                            @keyup="calculateClicks($event,'ctr1')"
                            @input="onPositiveInput('ctr1', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      Clicks = {{ clicks1 ? clicks1 : 0 }}
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(clicks1))" class="w-100">Copy Result
                    </dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetClicks()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'ctrCalc'">
          <div class="box-title">CTR Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Impressions</div>
                <dspInput v-model.number="impressions2"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="impressions2"
                          :data-vv-as="$t('Impressions')"
                          placeholder="Enter total impressions"
                          @keyup="calculateCtr($event,'impressions2')"
                          @input="onPositiveInput('impressions2', $event)"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Clicks</div>
                <dspInput v-model.number="clicks2"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="clicks2"
                          :data-vv-as="$t('Click')"
                          placeholder="Enter total clicks"
                          @keyup="calculateCtr($event,'clicks2')"
                          @input="onPositiveInput('clicks2', $event)"/>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      CTR = {{ ctr2 ? ctr2 : 0 }}%
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(ctr2))" class="w-100">Copy Result
                    </dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetCtr()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'cpmCalc'">
          <div class="box-title">CPM Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Impressions</div>
                <dspInput v-model.number="impressions3"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="impressions3"
                          :data-vv-as="$t('Impressions')"
                          placeholder="Enter total impressions"
                          @keyup="calculateCpm($event,'impressions3')"
                          @input="onPositiveInput('impressions3', $event)"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Total Cost</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="$">

                  <dspInput v-model="cost"
                            v-validate="{decimal:true,min_value:0}"
                            :errorsBag="errors"
                            name="cost"
                            :data-vv-as="$t('Cost')"
                            placeholder="Enter total cost"
                            @keyup="calculateCpm($event,'cost')"
                            @input="onPositiveInput('cost', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      CPM = {{ cpm ? cpm : 0 }}$
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(cpm))" class="w-100">Copy Result</dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetCpm()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'cpcCalc'">
          <div class="box-title">CPC Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Total Cost</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="$">

                  <dspInput v-model="cost1"
                            v-validate="{decimal:true,min_value:0}"
                            :errorsBag="errors"
                            name="cost1"
                            :data-vv-as="$t('Cost')"
                            placeholder="Enter total cost"
                            @keyup="calculateCpc($event,'cost1')"
                            @input="onPositiveInput('cost1', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Clicks</div>
                <dspInput v-model.number="clicks3"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="clicks3"
                          :data-vv-as="$t('Click')"
                          placeholder="Enter total clicks"
                          @keyup="calculateCpc($event,'clicks3')"
                          @input="onPositiveInput('clicks3', $event)"/>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      CPC = {{ cpc ? cpc : 0 }}$
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(cpc))" class="w-100">Copy Result</dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetCpc()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'cpaCalc'">
          <div class="box-title">CPA Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CPM</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="$">
                  <dspInput v-model.number="cpm1"
                            v-validate="{decimal:true,min_value:0}"
                            :errorsBag="errors"
                            name="cpm1"
                            :data-vv-as="$t('Cpm')"
                            :isDisabled="disableCPM"
                            placeholder="Enter total CPM"
                            @keyup="calculateCpa($event,'cpm1')"
                            @input="onPositiveInput('cpm1', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CPC</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="$">
                  <dspInput v-model.number="cpc1"
                            v-validate="{decimal:true,min_value:0}"
                            :errorsBag="errors"
                            name="cpc1"
                            :isDisabled="disableCPC"
                            :data-vv-as="$t('CPC')"
                            placeholder="Enter total CPC"
                            @keyup="calculateCpa($event,'cpc1')"
                            @input="onPositiveInput('cpc1', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CTR</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="%">
                  <dspInput v-model.number="ctr3"
                            v-validate="'max_value:100|min_value:0'"
                            :errorsBag="errors"
                            name="ctr3"
                            :isDisabled="disableCTR"
                            :data-vv-as="$t('Ctr')"
                            placeholder="Enter total CTR"
                            @keyup="calculateCpa($event,'ctr3')"
                            @input="onPositiveInput('ctr3', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="group-title">{{ $t('Conversion rate') }}</label>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="%">
                  <dspInput v-model.number="conversionRate"
                            v-validate="'max_value:100|min_value:0'"
                            :errorsBag="errors"
                            name="conversionRate"
                            :data-vv-as="$t('Conversion rate')"
                            placeholder="Enter total conversion rate"
                            @keyup="calculateCpa($event,'conversionRate')"
                            @input="onPositiveInput('conversionRate', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      CPA = {{ cpa ? cpa : 0 }}$
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(cpa))" class="w-100">Copy Result</dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetCpa()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
        <box class="profile-box calc-box" v-show="calcName === 'costCalc'">
          <div class="box-title">Total Cost Calculator</div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">Impressions</div>
                <dspInput v-model.number="impressions4"
                          v-validate="{integer:true,min_value:0}"
                          :errorsBag="errors"
                          name="impressions4"
                          :data-vv-as="$t('Impressions')"
                          placeholder="Enter total impressions"
                          @keyup="calculateCost($event,'impressions4')"
                          @input="onPositiveInput('impressions4', $event)"/>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <div class="group-title">CPM</div>
                <inputGroupRight class="short-append alt-error full-width w-100 text-left" append="$">
                  <dspInput v-model.number="cpm2"
                            v-validate="{decimal:true,min_value:0}"
                            :errorsBag="errors"
                            name="cpm2"
                            :data-vv-as="$t('Cpm')"
                            placeholder="Enter total CPM"
                            @keyup="calculateCost($event,'cpm2')"
                            @input="onPositiveInput('cpm2', $event)"/>
                </inputGroupRight>
              </div>
            </div>
            <div class="col-md-12">
              <div class="result-wrap">
                <div class="row">
                  <div class="col-md-4">
                    <div class="result">
                      Total Cost = {{ cost2 ? cost2 : 0 }}$
                    </div>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="copyToClipboard(decodeURIComponent(cost2))" class="w-100">Copy Result
                    </dsp-button>
                  </div>
                  <div class="col-md-4">
                    <dsp-button @click="resetCost()" class="w-100">Reset</dsp-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </box>
      </div>
      <div class="col-md-7">
        <box class="profile-box tips">
          <div class="box-title">Quick Tips</div>
          <p>Ensure your input data is accurate for reliable results.</p>
          <p>CTR is a good indicator of ad relevance and creative appeal.</p>
          <p>CPM is useful for comparing the cost-effectiveness of different publishers or placements.</p>
          <p>CPC helps you understand the direct cost of driving traffic to your site.</p>
          <p>CPA is crucial for evaluating the profitability of your campaigns based on desired actions.</p>
          <p>Total Cost gives you the overall expenditure for your ad efforts.</p>
        </box>
        <box class="profile-box tips">
          <div class="box-title">Metric Definitions</div>
          <table class="metric-table">
            <tr>
              <th>Metric</th>
              <th>Definitions</th>
            </tr>
            <tr>
              <td>Impressions</td>
              <td>The number of times an ad is shown.</td>
            </tr>
            <tr>
              <td>Clicks</td>
              <td>The number of times users clicked on an ad.</td>
            </tr>
            <tr>
              <td>CTR</td>
              <td>Click–Through Rate: Percentage of impressions that resulted in a click.</td>
            </tr>
            <tr>
              <td>CPM</td>
              <td>Cost Per Thousand Impressions: The cost advertisers pay for one thousand ad impressions.</td>
            </tr>
            <tr>
              <td>CPC</td>
              <td>Cost Per Click: The cost advertisers pay each time a user clicks on the ad.</td>
            </tr>
            <tr>
              <td>Total Cost</td>
              <td>The total amount spent on an ad campaign.</td>
            </tr>
            <tr>
              <td>Conversion Rate</td>
              <td>Percentage of clicks that resulted in a conversion.</td>
            </tr>
          </table>
        </box>
      </div>
    </div>
  </div>
</template>

<script>

import { integer, max_value } from 'vee-validate/dist/rules.esm';

export default {
  name: 'AdMetricsCalculator',

  data: function () {
    return {
      calcName: 'impCalc',
      impressions: '',
      impressions1: '',
      impressions2: '',
      impressions3: '',
      impressions4: '',
      conversions: '',
      cpc: '',
      cpc1: '',
      ctr: '',
      ctr1: '',
      ctr2: '',
      ctr3: '',
      clicks: '',
      clicks1: '',
      clicks2: '',
      clicks3: '',
      cost: '',
      cost1: '',
      cost2: '',
      cpm: '',
      cpm1: '',
      cpm2: '',
      cpm3: '',
      cpa: '',
      conversionRate: '',

      disableCPC: false,
      disableCPM: false,
      disableCTR: false,

    };
  },
  computed: {
    integer () {
      return integer;
    },
    max_value () {
      return max_value;
    },
  },

  created () {
    this.$validator.localize({
      en: {
        messages: {
          decimal: field => `The  ${field} must be a float number`,
        },
      },
    });
  },

  mounted () {
    this.$validator.localize({
      en: {
        messages: {
          integer: field => this.$t('The ' + field + ' field must be a whole number.')
        }
      },
    });
  },

  methods: {
    changeCalc (calcName) {
      this.calcName = calcName;

    },
    copyToClipboard (tagName) {
      this.$copyText(tagName);
      if (tagName === '') {
        this.$notify.info(this.$t('Nothing to copy'));
      } else {
        this.$notify.info(this.$t('Value copied'));
      }

    },
    resetImp () {
      this.clicks = '';
      this.ctr = '';
      this.impressions = '';
    },
    resetClicks () {
      this.clicks1 = '';
      this.ctr1 = '';
      this.impressions1 = '';
    },
    resetCtr () {
      this.clicks2 = '';
      this.ctr2 = '';
      this.impressions2 = '';
    },
    resetCpm () {
      this.cpm = '';
      this.cost = '';
      this.impressions3 = '';
    },
    resetCpc () {
      this.cpc = '';
      this.cost1 = '';
      this.clicks3 = '';
    },
    resetCpa () {
      this.cpm1 = '';
      this.cpc1 = '';
      this.ctr3 = '';
      this.cpa = '';
      this.conversionRate = '';
      this.disableCPC = false;
      this.disableCPM = false;
      this.disableCTR = false;
    },
    resetCost () {
      this.cpm2 = '';
      this.cost2 = '';
      this.impressions4 = '';
    },

    calculateImpressions (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {
        this[field] = event;
      } else {
        this[field] = '';
        this.impressions = '';
        return;
      }

      switch (field) {
        case 'clicks':
          if (this.ctr != '') {
            this.impressions = this.ctr != 0 ? Number.parseInt(this.clicks / (this.ctr / 100)) : '';
          }
          break;
        case 'ctr':
          if (this.clicks != '') {
            this.impressions = this.ctr !== 0 && this.clicks !== 0 ? Number.parseInt(this.clicks / (this.ctr / 100)) : '';
          }
          break;
      }
    },

    calculateClicks (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {
        this[field] = event;
      } else {
        this[field] = '';
        this.clicks1 = '';
        return;
      }

      switch (field) {
        case 'impressions1':
          if (this.ctr1 != '') {
            this.clicks1 = this.ctr1 !== '' ? this.impressions1 * this.ctr1 / 100 : '';
          }
          break;
        case 'ctr1':
          // this.$nextTick(() => {
          //   this.$validator.validate('ctr1', this.ctr1);
          // });
          if (this.impressions1 != '') {
            this.clicks1 = Number.parseInt(this.impressions1 * this.ctr1 / 100);
          }
          break;
      }
    },
    calculateCtr (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {
        this[field] = event;
      } else {
        this[field] = '';
        this.ctr2 = '';
        return;
      }

      switch (field) {
        case 'impressions2':
          if (this.clicks2 != '') {
            this.ctr2 = !isNaN(parseInt(this.clicks2)) && parseInt(this.clicks2) !== 0 && parseInt(this.impressions2) !== 0 ? Number.parseFloat((parseFloat(this.clicks2) / parseFloat(this.impressions2)) * 100).toFixed(2) : '';
          }
          break;
        case 'clicks2':
          if (this.impressions2 != '') {
            this.ctr2 = this.clicks2 != 0 && this.impressions2 != 0 ? Number.parseFloat((parseFloat(this.clicks2) / parseFloat(this.impressions2)) * 100).toFixed(2) : '';
          }
          break;
      }
    },

    calculateCpm (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {
        this[field] = event;
      } else {
        this[field] = '';
        this.cpm = '';
        return;
      }

      switch (field) {
        case 'impressions3':
          if (this.cost != '') {
            this.cpm = !isNaN(parseInt(this.cost)) && parseFloat(this.cost) !== 0 && parseInt(this.impressions3) !== 0 ? Number.parseFloat(this.cost / this.impressions3 * 1000).toFixed(2) : '';
          }
          break;
        case 'cost':
          if (this.impressions3 != '') {
            this.cpm = this.cost != 0 && this.impressions3 != 0 ? Number.parseFloat(this.impressions3 / this.cost * 1000).toFixed(2) : '';
          }
          break;
      }
    },

    calculateCpc (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {
        this[field] = event;
      } else {
        this[field] = '';
        this.cpc = '';
        return;
      }

      switch (field) {
        case 'cost1':
          if (this.clicks3 != '') {
            this.cpc = this.clicks3 != 0 ? Number.parseFloat(this.cost1 / this.clicks3).toFixed(2) : '';
          }
          break;
        case 'clicks3':
          if (this.cost1 != '') {
            this.cpc = Number.parseFloat(this.cost1 * this.clicks3).toFixed(4);
          }
          break;
      }
    },

    calculateCpa (event, field) {
      if (!isNaN(parseFloat(event)) && parseFloat(event) !== 0 && parseFloat(event) > 0) {

        this[field] = event;
      } else {
        this[field] = '';
        this.cpa = '';
        if (this.cpm1 === '') {
          this.disableCPC = false;
        }
        if (this.cpc1 === '') {
          this.disableCPM = this.disableCTR = false;
        }

        return;
      }

      switch (field) {
        case 'cpm1':
          this.disableCPC = !!this.cpm1;
          if (this.conversionRate !== '' && this.ctr3 !== '') {
            this.cpa = ((this.cpm1 / 1000) / (this.ctr3 * this.conversionRate)).toFixed(4);
          }
          break;

        case 'cpc1':
          this.disableCPM = this.disableCTR = !!this.cpc1;
          this.ctr3 = '';
          if (this.conversionRate !== '' && this.conversionRate != 0) {
            this.cpa = (this.cpc1 / this.conversionRate).toFixed(4);
          }
          break;

        case 'ctr3':
          if (this.cpm1 !== '' && this.conversionRate !== '' && this.ctr3 !== '' && parseFloat(this.ctr3) !== 0) {
            this.cpa = ((this.cpm1 / 1000) / (this.ctr3 * this.conversionRate)).toFixed(4);
          } else {
            this.cpa = '';
          }
          break;

        case 'conversionRate':
          if (this.conversionRate === '') {
            this.cpa = '';
            break;
          }
          if (this.cpm1 !== '' && this.ctr3 !== '') {
            this.cpa = ((this.cpm1 / 1000) / (this.ctr3 * this.conversionRate)).toFixed(4);
          }
          if (this.cpc1 !== '') {
            this.cpa = (this.cpc1 / this.conversionRate).toFixed(4);
          }
          break;
      }

    },
    calculateCost (event, field) {
      const value = parseFloat(event);

      if (!isNaN(value) && value !== 0 && parseFloat(event) > 0) {
        this[field] = value;
      } else {
        this[field] = '';
        this.cost2 = '';
        return;
      }

      switch (field) {
        case 'impressions4':
          if (this.cpm2 !== '') {
            this.cost2 = ((this.impressions4 / 1000) * this.cpm2).toFixed(2);
          }
          break;
        case 'cpm2':
          if (this.impressions4 !== '') {
            this.cost2 = ((this.impressions4 / 1000) * this.cpm2).toFixed(2);
          }
          break;
      }
    },

    onPositiveInput (field, value) {
      if (value === '') {
        return;
      }
      if (typeof value === 'string' && value.includes('-')) {
        value = value.replace(/-/g, '');
      }

      value = Number(value);
      if (isNaN(value) || value < 0) value = 0;
      this[field] = value;
    },
  }
};
</script>

<style lang="scss">
@import '@/scss/components/form-group';

@include profile-form-group;
</style>

<style lang="scss" scoped>
.sub-title {
  font-size: 12px;
}

::v-deep .profile-box {
  padding-bottom: 20px;

  &.tips {
    p {
      font-size: 12px;
    }
  }

  .box-title {
    font-size: 16px;
    line-height: 24px;
    color: #212B36;
    padding-bottom: 10px;
  }

  .btn-metrics-wrap {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    .button {
      margin-top: 10px;
      margin-bottom: 10px;
      width: 30%;
    }
  }

  .bitton.w-100 {
    margin-top: 10px;
  }

  .metric-table {
    width: 100%;
    font-size: 12px;

    tr {
      th {
        font-weight: 600;
        height: 40px;

        &:first-child {
          padding-right: 40px;
        }
      }

      td {
        height: 40px;
        border-top: 1px solid #ECEDF1;

        &:first-child {
          padding-right: 40px;
        }
      }
    }
  }
}

.short-append.input-group {
  &.full-width {
    .input-wrap ::v-deep {
      flex-basis: 100%;

      .input-text {
        width: 100%;
      }
    }

  }
}

.calc-box {
  .result-wrap {
    margin-top: 10px;
    padding-top: 20px;
    border-top: 1px solid #ECEDF1;

    .result {
      padding-top: 7px;
      font-size: 16px;
      font-weight: 600;
    }
  }
}
</style>
